import { Controller } from "@hotwired/stimulus";
import { fetchSearchCount } from "../../src/utils/fetchSearchCount";

// Connects to data-controller="job-filter--main"
export default class extends Controller {
  static targets = [
    "sortKey",
    "sortSelect",
    "form",
    "employmentStatus",
    "annualSalarySection",
    "hourlyWageSection",
    "buttonWrap",
    "stickyTrigger",
    "searchCount",
    "searchCountUnit",
    "submitButton",
  ];

  declare readonly sortKeyTarget: HTMLInputElement;
  declare readonly sortSelectTarget: HTMLSelectElement;
  declare readonly formTarget: HTMLFormElement;
  declare readonly employmentStatusTargets: HTMLInputElement[];
  declare readonly annualSalarySectionTarget: HTMLElement;
  declare readonly hourlyWageSectionTarget: HTMLElement;
  declare readonly buttonWrapTarget: HTMLElement;
  declare readonly stickyTriggerTarget: HTMLElement;
  declare readonly searchCountTarget: HTMLElement;
  declare readonly searchCountUnitTarget: HTMLElement;
  declare readonly submitButtonTarget: HTMLButtonElement;

  declare hasAnnualSalarySectionTarget: boolean;
  declare hasHourlyWageSectionTarget: boolean;
  declare hasButtonWrapTarget: boolean;
  declare hasStickyTriggerTarget: boolean;

  debounceTimeout = 0; // デバウンス管理用

  connect(): void {
    this.initStickyButton();
  }

  // キーワード blur イベント
  handleKeywordBlur(_event: Event): void {
    this.fetchSearchCountWithDebounce();
  }

  // 雇用形態 change イベント
  handleEmploymentStatusChange(event: Event): void {
    this.fetchSearchCountWithDebounce();
    this.toggleAnnualSalaryAndHourlyWage(event);
  }

  // 給与 change イベント
  handleSalaryChange(_event: Event): void {
    this.fetchSearchCountWithDebounce();
  }

  // 時給 change イベント
  handleHourlyWageChange(_event: Event): void {
    this.fetchSearchCountWithDebounce();
  }

  // 業種 change イベント
  handleIndustryChange(_event: Event): void {
    this.fetchSearchCountWithDebounce();
  }

  // 職種 change イベント
  handleOccupationChange(_event: Event): void {
    this.fetchSearchCountWithDebounce();
  }

  // 勤務地 change イベント
  handleWorkPlaceChange(_event: Event): void {
    this.fetchSearchCountWithDebounce();
  }

  // 従業員数 change イベント
  handleWorkerCountChange(_event: Event): void {
    this.fetchSearchCountWithDebounce();
  }

  // こだわり条件 change イベント
  handleSpecialConditionChange(_event: Event): void {
    this.fetchSearchCountWithDebounce();
  }

  // 表示条件 change イベント
  handleViewConditionChange(_event: Event): void {
    this.fetchSearchCountWithDebounce();
  }

  // 検索件数を取得する(デバウンスあり)
  private async fetchSearchCountWithDebounce() {
    clearTimeout(this.debounceTimeout);

    this.debounceTimeout = window.setTimeout(async () => {
      const count = await fetchSearchCount({ form: this.formTarget });
      this.updateSearchCount(Number(count));
      this.submitButtonTarget.disabled = Number(count) <= 0;
    }, 500);
  }

  // 検索件数表示を更新する
  private updateSearchCount(count: number): void {
    if (count > 999) {
      this.searchCountTarget.textContent = "999";
      this.searchCountUnitTarget.textContent = "件以上";
    } else {
      this.searchCountTarget.textContent = count.toString();
      this.searchCountUnitTarget.textContent = "件";
    }
  }

  // 検索ボタンのsticky化
  private initStickyButton(): void {
    if (!this.hasButtonWrapTarget || !this.hasStickyTriggerTarget) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 0) {
          this.buttonWrapTarget.classList.add("is_sticky");
        } else {
          this.buttonWrapTarget.classList.remove("is_sticky");
        }
      },
      { threshold: [0] },
    );

    observer.observe(this.stickyTriggerTarget);
  }

  // ソートによる求人検索
  searchBySort(event: Event): void {
    event.preventDefault();

    // ソートの値をhiddenフィールドにセット
    this.sortKeyTarget.value = this.sortSelectTarget.value;
    this.formTarget.requestSubmit();
  }

  // 給与・時給セクションの表示を切り替える
  private toggleAnnualSalaryAndHourlyWage(_event: Event): void {
    if (!this.hasAnnualSalarySectionTarget) return;
    if (!this.hasHourlyWageSectionTarget) return;

    // チェックされている雇用形態のボックスを取得
    const checkedEmploymentStatuses = this.employmentStatusTargets.filter((target) => target.checked);

    // アルバイトがチェックされているかチェック
    const partTimeChecked = checkedEmploymentStatuses.some((target) => target.value === "part_time");

    // アルバイトのみ選択されているかチェック
    const onlyPartTimeSelected =
      checkedEmploymentStatuses.length === 1 && checkedEmploymentStatuses[0].value === "part_time";

    // 表示ロジック
    if (onlyPartTimeSelected) {
      // アルバイトのみ選択時は年収非表示、時給表示
      this.annualSalarySectionTarget.style.display = "none";
      this.hourlyWageSectionTarget.style.display = "block";
    } else {
      // それ以外の場合
      // 年収セクションは常に表示
      this.annualSalarySectionTarget.style.display = "block";

      // アルバイトが含まれていれば時給表示、なければ時給非表示
      if (partTimeChecked) {
        this.hourlyWageSectionTarget.style.display = "block";
      } else {
        this.hourlyWageSectionTarget.style.display = "none";
      }
    }
  }

  // フォームリセット
  // MEMO: 複数の要素を操作するため querySelectorAll を使用
  resetForm(event: Event): void {
    event.preventDefault();

    // チェックボックスの初期化
    const checkboxes = this.formTarget.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((element: Element) => {
      const checkbox = element as HTMLInputElement;
      checkbox.checked = false;
    });

    // セレクトボックスの初期化
    const selects = this.formTarget.querySelectorAll("select");
    selects.forEach((element: Element) => {
      const select = element as HTMLSelectElement;
      select.selectedIndex = 0;
    });

    // テキスト入力欄の初期化
    const textInputs = this.formTarget.querySelectorAll('input[type="text"]');
    textInputs.forEach((element: Element) => {
      const input = element as HTMLInputElement;
      input.value = "";
    });

    // 隠しフィールドのリセット
    const hiddenFields = this.formTarget.querySelectorAll('input[type="hidden"]');
    hiddenFields.forEach((element: Element) => {
      const field = element as HTMLInputElement;
      field.value = "";
    });

    // モーダル関連の表示テキストをリセット
    const modalSpans = this.formTarget.querySelectorAll(".open_modal span");
    modalSpans.forEach((element: Element) => {
      const span = element as HTMLSpanElement;
      span.textContent = "指定なし";
    });

    // 給与セクションの表示状態をリセット
    if (this.hasHourlyWageSectionTarget) {
      this.hourlyWageSectionTarget.style.display = "none";
    }

    if (this.hasAnnualSalarySectionTarget) {
      this.annualSalarySectionTarget.style.display = "block";
    }

    // リセット後に検索件数を更新
    this.fetchSearchCountWithDebounce();
  }
}
